/**
 * Enhanced storage utility with fallback mechanisms and error recovery
 * Handles localStorage failures gracefully with fallback to sessionStorage
 */

type StorageType = 'localStorage' | 'sessionStorage';

interface StorageResult<T> {
  success: boolean;
  data: T | null;
  error?: string;
  usedFallback: boolean;
}

class AuthStorage {
  private fallbackEnabled = true;
  private storageQuotaExceeded = false;

  /**
   * Set an item with automatic fallback to sessionStorage
   */
  setItem<T>(key: string, value: T, useSessionStorage = false): StorageResult<T> {
    const storage = useSessionStorage ? sessionStorage : localStorage;

    try {
      const serialized = JSON.stringify(value);
      storage.setItem(key, serialized);
      return { success: true, data: value, usedFallback: false };
    } catch (error) {
      // Handle different storage errors
      if (error instanceof Error) {
        if (error.name === 'QuotaExceededError') {
          this.storageQuotaExceeded = true;
          return this.handleQuotaExceeded(key, value);
        } else if (error.name === 'SecurityError') {
          console.warn('[AuthStorage] Security error, trying fallback storage');
          if (!useSessionStorage && this.fallbackEnabled) {
            return this.setItem(key, value, true); // Try sessionStorage
          }
        }
      }

      console.error('[AuthStorage] Failed to set item:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
        usedFallback: false
      };
    }
  }

  /**
   * Get an item, trying both localStorage and sessionStorage
   */
  getItem<T>(key: string): StorageResult<T> {
    // Try localStorage first
    try {
      const item = localStorage.getItem(key);
      if (item !== null) {
        const parsed = JSON.parse(item) as T;
        return { success: true, data: parsed, usedFallback: false };
      }
    } catch (error) {
      console.warn('[AuthStorage] Failed to read from localStorage:', error);
    }

    // Try sessionStorage as fallback
    if (this.fallbackEnabled) {
      try {
        const item = sessionStorage.getItem(key);
        if (item !== null) {
          const parsed = JSON.parse(item) as T;
          return { success: true, data: parsed, usedFallback: true };
        }
      } catch (error) {
        console.warn('[AuthStorage] Failed to read from sessionStorage:', error);
      }
    }

    return { success: true, data: null, usedFallback: false };
  }

  /**
   * Remove an item from both storage types
   */
  removeItem(key: string): StorageResult<null> {
    let localStorageError: Error | null = null;
    let sessionStorageError: Error | null = null;
    let removedFromLocalStorage = false;
    let removedFromSessionStorage = false;

    // Remove from localStorage
    try {
      localStorage.removeItem(key);
      removedFromLocalStorage = true;
    } catch (error) {
      localStorageError = error instanceof Error ? error : new Error('Unknown error');
      console.warn('[AuthStorage] Failed to remove from localStorage:', error);
    }

    // Remove from sessionStorage
    try {
      sessionStorage.removeItem(key);
      removedFromSessionStorage = true;
    } catch (error) {
      sessionStorageError = error instanceof Error ? error : new Error('Unknown error');
      console.warn('[AuthStorage] Failed to remove from sessionStorage:', error);
    }

    const success = removedFromLocalStorage || removedFromSessionStorage;
    const error = localStorageError || sessionStorageError;

    return {
      success,
      data: null,
      error: error?.message,
      usedFallback: removedFromSessionStorage && !removedFromLocalStorage
    };
  }

  /**
   * Clear all auth-related items from both storage types
   */
  clearAuthData(keys: string[]): void {
    keys.forEach(key => {
      this.removeItem(key);
    });
  }

  /**
   * Handle storage quota exceeded error
   */
  private handleQuotaExceeded<T>(key: string, value: T): StorageResult<T> {
    console.warn('[AuthStorage] Storage quota exceeded, attempting cleanup');

    // Try to clear non-essential data first
    try {
      // Get all keys and filter for non-critical items
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && !storageKey.startsWith('finpay-')) {
          keysToRemove.push(storageKey);
        }
      }

      // Remove non-critical items
      keysToRemove.forEach(k => localStorage.removeItem(k));

      // Try storing the critical item again
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      console.log('[AuthStorage] Successfully stored after cleanup');
      return { success: true, data: value, usedFallback: false };
    } catch (error) {
      console.error('[AuthStorage] Still cannot store after cleanup:', error);

      // Last resort: try sessionStorage
      if (this.fallbackEnabled) {
        return this.setItem(key, value, true);
      }

      return {
        success: false,
        data: null,
        error: 'Storage quota exceeded and cleanup failed',
        usedFallback: false
      };
    }
  }

  /**
   * Check if storage is available
   */
  isStorageAvailable(type: StorageType = 'localStorage'): boolean {
    try {
      const testKey = '__auth_storage_test__';
      const storage = type === 'localStorage' ? localStorage : sessionStorage;
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage statistics
   */
  getStorageInfo(): {
    localStorageAvailable: boolean;
    sessionStorageAvailable: boolean;
    quotaExceeded: boolean;
    localStorageKeys: number;
    sessionStorageKeys: number;
  } {
    return {
      localStorageAvailable: this.isStorageAvailable('localStorage'),
      sessionStorageAvailable: this.isStorageAvailable('sessionStorage'),
      quotaExceeded: this.storageQuotaExceeded,
      localStorageKeys: localStorage.length,
      sessionStorageKeys: sessionStorage.length
    };
  }

  /**
   * Enable/disable fallback storage
   */
  setFallbackEnabled(enabled: boolean): void {
    this.fallbackEnabled = enabled;
  }
}

// Export singleton instance
export const authStorage = new AuthStorage();